import React from "react";

const FormattedMessage = ({ content }) => {
    const formatText = (text) => {
        const codeBlockParts = text.split(/(```[\s\S]*?```)/g);

        return codeBlockParts.map((part, index) => {
            if (part.startsWith('```') && part.endsWith('```')) {
                const lines = part.slice(3, -3).split('\n');
                const language = lines[0].trim();
                const code = lines.slice(language ? 1 : 0).join('\n');

                return (
                    <div key={index} className="my-3 sm:my-4 w-full min-w-0">
                        <pre className="bg-slate-800 text-green-400 p-3 sm:p-4 rounded-xl shadow-lg border border-slate-600 overflow-x-auto max-w-full">
                            {language && (
                                <div className="text-slate-400 text-xs mb-2 font-mono uppercase tracking-wide">{language}</div>
                            )}
                            <code className="text-xs sm:text-sm font-mono whitespace-pre block">
                                {code}
                            </code>
                        </pre>
                    </div>
                );
            } else {
                return (
                    <div key={index}>
                        {formatRegularText(part)}
                    </div>
                );
            }
        });
    };

    const formatRegularText = (text) => {
        const paragraphs = text.split(/\n\s*\n/);

        return paragraphs.map((paragraph, pIndex) => {
            const trimmedParagraph = paragraph.trim();
            if (!trimmedParagraph) return null;

            const lines = trimmedParagraph.split('\n');
            const isListSection = lines.some(line =>
                line.trim().match(/^[-*+]\s/) ||
                line.trim().match(/^\d+\.\s/) ||
                line.trim().match(/^[a-zA-Z]\.\s/)
            );

            if (isListSection) {
                return (
                    <div key={pIndex} className="my-3">
                        {formatListSection(lines)}
                    </div>
                );
            } else {
                return (
                    <p key={pIndex} className="mb-3 last:mb-0 leading-relaxed">
                        {formatInlineText(trimmedParagraph)}
                    </p>
                );
            }
        }).filter(Boolean);
    };

    const formatListSection = (lines) => {
        const result = [];
        let currentList = [];
        let currentListType = null;

        lines.forEach((line, index) => {
            const trimmedLine = line.trim();

            if (!trimmedLine) return;

            const bulletMatch = trimmedLine.match(/^[-*+]\s(.+)/);
            const numberedMatch = trimmedLine.match(/^\d+\.\s(.+)/);
            const letterMatch = trimmedLine.match(/^[a-zA-Z]\.\s(.+)/);

            if (bulletMatch || numberedMatch || letterMatch) {
                const content = (bulletMatch || numberedMatch || letterMatch)[1];
                const listType = bulletMatch ? 'ul' : 'ol';

                if (currentListType && currentListType !== listType) {
                    if (currentList.length > 0) {
                        result.push(renderList(currentList, currentListType, result.length));
                        currentList = [];
                    }
                }

                currentListType = listType;
                currentList.push(content);
            } else {
                if (currentList.length > 0) {
                    result.push(renderList(currentList, currentListType, result.length));
                    currentList = [];
                    currentListType = null;
                }

                if (trimmedLine.endsWith(':')) {
                    result.push(
                        <h4 key={`header-${result.length}`} className="font-semibold text-white mt-4 mb-2 first:mt-0">
                            {formatInlineText(trimmedLine)}
                        </h4>
                    );
                } else {
                    result.push(
                        <p key={`text-${result.length}`} className="mb-2 leading-relaxed">
                            {formatInlineText(trimmedLine)}
                        </p>
                    );
                }
            }
        });

        if (currentList.length > 0) {
            result.push(renderList(currentList, currentListType, result.length));
        }

        return result;
    };

    const renderList = (items, type, key) => {
        const ListComponent = type === 'ul' ? 'ul' : 'ol';
        const listClasses = type === 'ul'
            ? "list-disc list-inside ml-4 space-y-2"
            : "list-decimal list-inside ml-4 space-y-2";

        return (
            <ListComponent key={key} className={listClasses}>
                {items.map((item, itemIndex) => (
                    <li key={itemIndex} className="text-slate-200 leading-relaxed">
                        {formatInlineText(item)}
                    </li>
                ))}
            </ListComponent>
        );
    };

    const formatInlineText = (text) => {
        const parts = text.split(/(`[^`\n]+`)/g);

        return parts.map((part, index) => {
            if (part.startsWith('`') && part.endsWith('`')) {
                return (
                    <code key={index} className="bg-slate-700 px-2 py-1 rounded-md text-sm font-mono text-emerald-400 mx-1 break-all">
                        {part.slice(1, -1)}
                    </code>
                );
            }

            const boldFormatted = part.split(/(\*\*[^*\n]+\*\*|__[^_\n]+__)/g).map((boldPart, boldIndex) => {
                if ((boldPart.startsWith('**') && boldPart.endsWith('**')) ||
                    (boldPart.startsWith('__') && boldPart.endsWith('__'))) {
                    return (
                        <strong key={boldIndex} className="font-semibold text-white">
                            {boldPart.slice(2, -2)}
                        </strong>
                    );
                }

                const italicFormatted = boldPart.split(/(\*[^*\n]+\*|_[^_\n]+_)/g).map((italicPart, italicIndex) => {
                    if (((italicPart.startsWith('*') && italicPart.endsWith('*')) ||
                        (italicPart.startsWith('_') && italicPart.endsWith('_'))) &&
                        !italicPart.includes('**') && !italicPart.includes('__')) {
                        return (
                            <em key={italicIndex} className="italic text-slate-300">
                                {italicPart.slice(1, -1)}
                            </em>
                        );
                    }
                    return italicPart;
                });

                return italicFormatted;
            });

            return boldFormatted;
        });
    };

    return (
        <div className="formatted-message message-content text-slate-200 min-w-0">
            {formatText(content)}
        </div>
    );
};

export default FormattedMessage;